import React, { useState, useMemo } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase.js';
import * as utils from '../../common/Utils.js';

const EMPTY_DAY = { date: '', startTime: '09:00', endTime: '21:00' };

const DraftScheduleEditor = ({ season, currentSchedule, requiredPicks, onSaved }) => {
  const initial = currentSchedule || { pickIntervalMinutes: 60, days: [{ ...EMPTY_DAY }] };
  const [schedule, setSchedule] = useState(initial);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const generatedCount = useMemo(
    () => utils.generateTimeSlotsFromSchedule(schedule).length,
    [schedule]
  );

  const setInterval = (val) => {
    const mins = Math.max(1, parseInt(val) || 60);
    setSchedule(s => ({ ...s, pickIntervalMinutes: mins }));
  };

  const updateDay = (index, field, value) => {
    setSchedule(s => {
      const days = [...s.days];
      days[index] = { ...days[index], [field]: value };
      return { ...s, days };
    });
  };

  const addDay = () => {
    setSchedule(s => ({ ...s, days: [...s.days, { ...EMPTY_DAY }] }));
  };

  const removeDay = (index) => {
    setSchedule(s => ({ ...s, days: s.days.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'draft', season), { draftSchedule: schedule });
      onSaved(schedule);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const picksOk = generatedCount >= requiredPicks;

  return (
    <div>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={() => setOpen(o => !o)}
      >
        {open ? 'Hide Schedule Editor' : 'Edit Draft Schedule'}
      </button>

      {open && (
        <div
          style={{
            marginTop: 12, padding: '16px 20px', border: '1px solid #ddd',
            borderRadius: 8, background: '#fafafa', maxWidth: 620,
          }}
        >
          <p className="league-title-standard-small" style={{ marginBottom: 12 }}>
            Draft Schedule
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <label style={{ fontSize: 14 }}>Minutes between picks:</label>
            <input
              type="number"
              min={1}
              value={schedule.pickIntervalMinutes}
              onChange={e => setInterval(e.target.value)}
              className="form-control"
              style={{ width: 80 }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {schedule.days.map((day, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                  gap: 8, padding: '10px 12px', background: '#fff',
                  border: '1px solid #ddd', borderRadius: 6,
                }}
              >
                <span style={{ fontSize: 13, color: '#888', minWidth: 20 }}>
                  Day {i + 1}
                </span>
                <input
                  type="date"
                  value={day.date}
                  onChange={e => updateDay(i, 'date', e.target.value)}
                  className="form-control"
                  style={{ width: 150 }}
                />
                <input
                  type="time"
                  value={day.startTime}
                  onChange={e => updateDay(i, 'startTime', e.target.value)}
                  className="form-control"
                  style={{ width: 120 }}
                />
                <span style={{ fontSize: 13 }}>to</span>
                <input
                  type="time"
                  value={day.endTime}
                  onChange={e => updateDay(i, 'endTime', e.target.value)}
                  className="form-control"
                  style={{ width: 120 }}
                />
                <button
                  className="btn btn-sm btn-outline-danger"
                  disabled={schedule.days.length === 1}
                  onClick={() => removeDay(i)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={addDay}
            style={{ marginBottom: 14 }}
          >
            + Add Day
          </button>

          <div
            style={{
              padding: '10px 14px', borderRadius: 6, marginBottom: 14, fontSize: 14,
              background: picksOk ? '#d4edda' : '#f8d7da',
              color: picksOk ? '#155724' : '#721c24',
              border: `1px solid ${picksOk ? '#c3e6cb' : '#f5c6cb'}`,
            }}
          >
            This schedule generates <strong>{generatedCount} picks</strong>
            {requiredPicks > 0 && (
              <> — you need <strong>{requiredPicks}</strong>
                {picksOk
                  ? ` ✓ (${generatedCount - requiredPicks} extra)`
                  : ` ✗ (${requiredPicks - generatedCount} short)`}
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-dark btn-sm"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save Schedule'}
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftScheduleEditor;
